import axios from "axios";
import { EventEmitter } from "events";
import { createContext, useContext, useState } from "react";

const MemberContext = createContext();

const MemberEvents = new EventEmitter();

export const MemberProvider = ({ children }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0,
  });

  // `search` : nom / téléphone. `partnerId` : filtre partenaire (admin uniquement ;
  // vide → toutes les organisations). Même contrat que les livreurs.
  const [searchParams, setSearchParams] = useState({
    search: "",
    partnerId: "",
  });

  const updatePagination = (newPagination) => {
    setPagination((prev) => ({ ...prev, ...newPagination }));
  };

  const updateSearchParams = (newParams) => {
    setSearchParams((prev) => ({ ...prev, ...newParams }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Liste paginée des membres (agents), filtrée serveur — parité `GET /riders`.
  // - Compte partenaire : `partnerId` omis → l'API résout l'organisation depuis
  //   son membership (jamais via l'URL).
  // - Admin plateforme : `partnerId` optionnel (filtre) — absent → toutes les orgs.
  const getMembers = async () => {
    try {
      setLoading(true);

      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (searchParams.search) params.search = searchParams.search;
      if (searchParams.partnerId) params.partnerId = searchParams.partnerId;

      const response = await axios.get(`/partner/members`, { params });
      const { data, total, totalPages } = response.data;

      setMembers(data);
      setPagination((prev) => ({ ...prev, total, totalPages }));
    } catch (error) {
      console.error("Error fetching partner members:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Invitation : crée le compte si le numéro est inconnu et envoie un mot de
  // passe provisoire par WhatsApp (rien à gérer côté front).
  const inviteMember = async ({ partnerId, phone, name, roleCode }) => {
    try {
      const payload = { phone };
      if (partnerId) payload.partnerId = partnerId;
      if (name) payload.name = name;
      if (roleCode) payload.roleCode = roleCode;
      const response = await axios.post(`/partner/members`, payload);
      const member = response.data;
      setMembers((prev) => [member, ...prev]);
      MemberEvents.emit("member:invited");
      return member;
    } catch (error) {
      console.error("Error inviting member:", error);
      throw error;
    }
  };

  // Modifier un agent (nom / téléphone). `partnerId` : requis côté admin, ignoré
  // côté partenaire. L'organisation d'un agent ne se réaffecte pas (1 membre =
  // 1 partenaire) → `partnerId` sert uniquement à cibler l'organisation.
  const updateMember = async (memberId, { partnerId, name, phone }) => {
    try {
      const payload = {};
      if (partnerId) payload.partnerId = partnerId;
      if (name !== undefined) payload.name = name;
      if (phone !== undefined) payload.phone = phone;
      const response = await axios.put(
        `/partner/members/${memberId}`,
        payload
      );
      const updated = response.data;
      setMembers((prev) =>
        prev.map((m) => (m.id === updated.id ? updated : m))
      );
      MemberEvents.emit("member:updated");
      return updated;
    } catch (error) {
      console.error("Error updating member:", error);
      throw error;
    }
  };

  // Réinitialise le mot de passe de l'agent → renvoie `{ password }` à communiquer.
  const resetMemberPassword = async (memberId, partnerId) => {
    try {
      const response = await axios.put(
        `/partner/members/${memberId}/password/reset`,
        null,
        { params: partnerId ? { partnerId } : undefined }
      );
      return response.data;
    } catch (error) {
      console.error("Error resetting member password:", error);
      throw error;
    }
  };

  // Déconnecte l'agent (invalide ses sessions) — `204`.
  const disconnectMember = async (memberId, partnerId) => {
    try {
      await axios.post(`/partner/members/${memberId}/disconnect`, null, {
        params: partnerId ? { partnerId } : undefined,
      });
    } catch (error) {
      console.error("Error disconnecting member:", error);
      throw error;
    }
  };

  // Suppression (soft delete) — l'agent disparaît des listes. Gardes backend :
  // pas d'auto-suppression, pas de suppression du dernier owner actif (403).
  const deleteMember = async (memberId, partnerId) => {
    try {
      await axios.delete(`/partner/members/${memberId}`, {
        params: partnerId ? { partnerId } : undefined,
      });
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      setPagination((prev) => ({
        ...prev,
        total: prev.total > 0 ? prev.total - 1 : 0,
        totalPages: Math.ceil((prev.total - 1) / prev.limit),
      }));
      MemberEvents.emit("member:updated");
    } catch (error) {
      console.error("Error deleting member:", error);
      throw error;
    }
  };

  // Suspendre / réactiver / retirer un membre. Chaque transition a son endpoint
  // dédié renvoyant 204 (pas de corps) — même pattern que les livreurs. On met à
  // jour le statut localement de façon optimiste.
  const setMemberStatusLocally = (memberId, status) => {
    setMembers((prev) =>
      prev.map((m) => (m.id === memberId ? { ...m, status } : m))
    );
    MemberEvents.emit("member:updated");
  };

  // `partnerId` (query) : requis pour un admin plateforme, omis pour un compte
  // partenaire (résolu backend depuis son membership).
  const activateMember = async (memberId, partnerId) => {
    try {
      await axios.put(`/partner/members/${memberId}/activate`, null, {
        params: partnerId ? { partnerId } : undefined,
      });
      setMemberStatusLocally(memberId, "active");
    } catch (error) {
      console.error("Error activating member:", error);
      throw error;
    }
  };

  const suspendMember = async (memberId, partnerId) => {
    try {
      // L'accès du membre est révoqué immédiatement côté backend.
      await axios.put(`/partner/members/${memberId}/suspend`, null, {
        params: partnerId ? { partnerId } : undefined,
      });
      setMemberStatusLocally(memberId, "suspended");
    } catch (error) {
      console.error("Error suspending member:", error);
      throw error;
    }
  };

  const deactivateMember = async (memberId, partnerId) => {
    try {
      await axios.put(`/partner/members/${memberId}/deactivate`, null, {
        params: partnerId ? { partnerId } : undefined,
      });
      setMemberStatusLocally(memberId, "deactivated");
    } catch (error) {
      console.error("Error deactivating member:", error);
      throw error;
    }
  };

  // Changer le rôle d'un membre (owner ↔ agent). Gardes backend : pas d'auto-
  // modification, pas de retrait du dernier owner actif.
  const updateMemberRole = async (memberId, roleCode, partnerId) => {
    try {
      const payload = { roleCode };
      if (partnerId) payload.partnerId = partnerId;
      const response = await axios.put(
        `/partner/members/${memberId}/role`,
        payload
      );
      const updated = response.data;
      setMembers((prev) =>
        prev.map((m) => (m.id === updated.id ? updated : m))
      );
      MemberEvents.emit("member:updated");
      return updated;
    } catch (error) {
      console.error("Error updating member role:", error);
      throw error;
    }
  };

  const value = {
    members,
    loading,
    pagination,
    searchParams,
    getMembers,
    inviteMember,
    updateMember,
    resetMemberPassword,
    disconnectMember,
    deleteMember,
    activateMember,
    suspendMember,
    deactivateMember,
    updateMemberRole,
    updatePagination,
    updateSearchParams,
    MemberEvents,
  };

  return (
    <MemberContext.Provider value={value}>{children}</MemberContext.Provider>
  );
};

export const useMemberContext = () => {
  const context = useContext(MemberContext);
  if (!context) {
    throw new Error("useMemberContext must be used within a MemberProvider");
  }
  return context;
};
